import { useCustomerDisplayName } from '@src/hooks';
import { useStrategies } from '@src/providers';
import {
  Box,
  Card,
  Divider,
  EllipsisBoxWithTooltip,
  FilledMeter,
  Flex,
  HStack,
  InlineFormattedNumber,
  ModeEnum,
  ParametersTable,
  PricingModeEnum,
  QuoteSide,
  QuoteStatusEnum,
  SecurityOrSecuritiesRenderer,
  Side,
  SmartSummary,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Tooltip,
  Tr,
  formatDuration,
  formattedUTCDay,
  isCustomerOrder,
  isCustomerQuote,
  isEntityRFQ,
  prettyName,
  useMarketsContext,
  type CustomerOrder,
  type CustomerQuote,
  type Duration,
} from '@talos/kyoko';
import Big from 'big.js';
import { intervalToDuration } from 'date-fns/esm';
import { useState } from 'react';
import { useHarmonicIntervalFn } from 'react-use';
import { FixingDetailsIndicesLabel } from '../OMS/NewRFQ/Components/FixingDetailsIndicesLabel';
import { NegotiationStatus, NegotiationStatusLabel } from './NegotiationStatus';

export interface CustomerSubmittedSummaryCardProps {
  entity?: CustomerOrder | CustomerQuote;
  showTitle?: boolean;
  showGroup?: boolean;
  showPricingMode?: boolean;
}

export function CustomerSubmittedSummaryCard({
  entity,
  showTitle = true,
  showGroup,
  showPricingMode,
}: CustomerSubmittedSummaryCardProps) {
  const { strategiesList: strategies } = useStrategies();
  const { marketsByName } = useMarketsContext();
  const customerName = useCustomerDisplayName(entity?.Counterparty);

  if (entity == null) {
    return null;
  }

  const strategy = isCustomerOrder(entity)
    ? strategies.find(strategy => entity?.Strategy === strategy.Name)
    : undefined;
  const isRFQ = isEntityRFQ(entity);

  return (
    <Card
      title={
        showTitle ? (
          <HStack gap="spacing8">
            <Text>{isCustomerOrder(entity) ? 'Order' : 'Quote'}</Text>
          </HStack>
        ) : null
      }
      actions={
        isCustomerOrder(entity) &&
        showTitle && (
          <Tooltip showUnderline={true} tooltip={<ParametersTable order={entity} />} placement="bottom">
            <Text color="colorTextSubtle" size="fontSizeSmall">
              Params
            </Text>
          </Tooltip>
        )
      }
    >
      <Flex flexDirection="column" gap="spacing8" fontSize="fontSizeSmall">
        <Box ml="-spacing4" mr="-spacing4">
          <Table w="100%">
            <Tbody>
              <Tr>
                <Td colSpan={2}>
                  <Flex alignItems="center" gap="spacing4">
                    {isCustomerOrder(entity) ? (
                      <Side side={entity.Side}>{entity.Side}</Side>
                    ) : (
                      <QuoteSide side={entity.Side} />
                    )}
                    <SecurityOrSecuritiesRenderer symbolOrSymbols={entity.symbolOrSymbols} showIcon={false} />
                    {entity.PricingMode === PricingModeEnum.SpreadToFixing && (
                      <>
                        <Text>/</Text>
                        <FixingDetailsIndicesLabel fixingDetails={entity.FixingDetails} />
                      </>
                    )}
                    <Text color="colorTextSubtle" style={{ textTransform: 'uppercase' }}>
                      {isRFQ ? 'RFQ' : strategy?.DisplayName}
                    </Text>
                  </Flex>
                </Td>
              </Tr>
              <Tr>
                <Td colSpan={2}>
                  <SmartSummary entity={entity} type={isCustomerOrder(entity) ? 'order' : 'quote'} showSide={false} />
                </Td>
              </Tr>
              <Tr>
                <Th>User</Th>
                <Td align="right" maxWidth="0">
                  <EllipsisBoxWithTooltip textAlign="right">
                    {entity.CustomerUser ?? entity.User}
                  </EllipsisBoxWithTooltip>
                </Td>
              </Tr>
              <Tr>
                <Th>Customer</Th>
                <Td align="right" maxWidth="0">
                  <EllipsisBoxWithTooltip textAlign="right">{customerName}</EllipsisBoxWithTooltip>
                </Td>
              </Tr>
              {showPricingMode && entity.PricingMode != null && (
                <Tr>
                  <Th>Pricing Mode</Th>
                  <Td align="right">{prettyName(entity.PricingMode)}</Td>
                </Tr>
              )}
              {showGroup && entity.Group != null && (
                <Tr>
                  <Td>Group</Td>
                  <Td align="right">{entity.Group}</Td>
                </Tr>
              )}
              {isCustomerQuote(entity) && entity.ClearingMarket != null && (
                <Tr>
                  <Th whiteSpace="nowrap">Clearing Market</Th>
                  <Td align="right">
                    {marketsByName.get(entity.ClearingMarket)?.DisplayName ?? entity.ClearingMarket}
                  </Td>
                </Tr>
              )}
              {isCustomerQuote(entity) && entity.NegotiationParameters?.SizeCountering === ModeEnum.Enabled && (
                <Tr>
                  <Th whiteSpace="nowrap">Size Countering</Th>
                  <Td align="right">Enabled</Td>
                </Tr>
              )}
              {isCustomerQuote(entity) && entity.NegotiationParameters?.PartialFill === ModeEnum.Enabled && (
                <Tr>
                  <Th whiteSpace="nowrap">Partial Fills</Th>
                  <Td align="right">Enabled</Td>
                </Tr>
              )}
              {'ExternalGroup' in entity && entity.ExternalGroup != null && (
                <Tr>
                  <Th>External ID</Th>
                  <Td color="colors.yellow.lighten" align="right">
                    {entity.ExternalGroup}
                  </Td>
                </Tr>
              )}
              {'ExternalComments' in entity && entity.ExternalComments != null && (
                <Tr>
                  <Th>External Comments</Th>
                  <Td align="right">{entity.ExternalComments}</Td>
                </Tr>
              )}
              {'SettleDate' in entity && entity.SettleDate != null && (
                <Tr>
                  <Th>Settlement Date</Th>
                  <Td align="right">{formattedUTCDay(entity.SettleDate)}</Td>
                </Tr>
              )}
              {isCustomerQuote(entity) && entity.EndTime != null && !entity.isComplete && (
                <Tr>
                  <Th>Ends in</Th>
                  <Td align="right">
                    <QuoteCountdown status={entity.QuoteStatus} endTime={entity.EndTime} />
                  </Td>
                </Tr>
              )}
              {'RFQProtocol' in entity && entity.RFQProtocol === 'Manual' && (
                <Tr>
                  <Th>
                    <NegotiationStatusLabel />
                  </Th>
                  <Td align="right">
                    <NegotiationStatus entity={entity} />
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>
        {
          /* Server sometimes isn't passing this sometimes, even though it's a required field */
          entity.OrderQty != null && isCustomerOrder(entity) && (
            <>
              <Divider />
              <Box ml="-spacing4" mr="-spacing4">
                <Table w="100%">
                  <Tbody>
                    <Tr>
                      <Td>Filled Qty</Td>
                      <Td align="right">
                        <InlineFormattedNumber
                          number={Big(entity.CumQty ?? '0')
                            .div(entity.OrderQty === '0' ? 1 : entity.OrderQty)
                            .times(100)
                            .toFixed(2)}
                          currency="%"
                        />{' '}
                        / <InlineFormattedNumber number={entity.CumQty} currency={entity.Currency} />
                      </Td>
                    </Tr>
                  </Tbody>
                </Table>
              </Box>
              <FilledMeter entity={entity} showLabel={true} isCustomerOrder={true} />
            </>
          )
        }
      </Flex>
    </Card>
  );
}

export function QuoteCountdown({ status, endTime }: { endTime: string; status: QuoteStatusEnum }) {
  const [millisecondsLeft, setMillisecondsLeft] = useState(() => Math.max(0, new Date(endTime).getTime() - Date.now()));
  useHarmonicIntervalFn(() => {
    setMillisecondsLeft(Math.max(0, new Date(endTime).getTime() - Date.now()));
  }, 1000);

  return (
    <Text color={millisecondsLeft === 0 ? 'colorTextSubtle' : undefined}>
      {millisecondsLeft === 0 || status === QuoteStatusEnum.Canceled
        ? 'Expired'
        : `${formatDuration(intervalToDuration({ start: 0, end: millisecondsLeft }) as Duration, '')}`}
    </Text>
  );
}
